import { compact, find, get } from 'lodash-es';
import { CHART_PROPS_REDESIGN, DEAL_LABEL, VEHICLE_SELECT_MODE_MAP } from 'site-modules/shared/constants/price-checker';
import {
  ABOVE_MARKET,
  FAIR_DEAL,
  GOOD_DEAL,
  GREAT_DEAL,
  HIGH_DEAL,
} from 'site-modules/shared/components/deal/deal-constants';
import { formatPriceString, getPriceString, roundToNearestTen } from 'site-modules/shared/utils/price-utils';
import {
  getPaymentsRange,
  getSliderClosestValue,
} from 'site-modules/shared/components/car-buying/offer-type-table/offer-type-table-chart-utils';
import { DEALS } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/digital-retail-offer-types';
import { LEASE, PURCHASE } from 'site-modules/shared/constants/car-buying';
import { getTotalRebates } from 'site-modules/shared/components/inventory/utils/get-total-rebates';
import { getEspTrustDate } from './esp';
import { getPriceData } from './pricing-chart';
import { showTrimName } from './show-trim-name';

const VDP_SUPPORTING_COPY = {
  [GREAT_DEAL]: { label: DEAL_LABEL.GREAT, index: 1, cta: 'Well below market average', dealType: GREAT_DEAL },
  [GOOD_DEAL]: { label: DEAL_LABEL.GOOD, index: 4, cta: 'Below market average', dealType: GOOD_DEAL },
  [FAIR_DEAL]: { label: DEAL_LABEL.FAIR, index: 7, cta: 'Around market average', dealType: FAIR_DEAL },
  [ABOVE_MARKET]: maxFairPrice => ({
    label: DEAL_LABEL.DEALER,
    index: 14,
    cta: `Edmunds suggests you pay: ${maxFairPrice && formatPriceString(maxFairPrice)}`,
    dealType: HIGH_DEAL,
  }),
};

/**
 * When initial param is defined then vehicleParams or vinParams should have value or to be null (after manual reset)
 */
export function hasVehicleData({ make, model, year, styleId, vehicleParams, vin, vinParams, vehicleSelectMode }) {
  const styleParamsReady =
    vehicleSelectMode !== VEHICLE_SELECT_MODE_MAP.MMY ||
    ((!make || vehicleParams.make || vehicleParams.make === null) &&
      (!(make && model) || vehicleParams.model || vehicleParams.model === null) &&
      (!(make && model && year) || vehicleParams.year || vehicleParams.year === null) &&
      (!(make && model && year && styleId) || vehicleParams.style || vehicleParams.style === null));

  const vinParamsReady =
    vehicleSelectMode !== VEHICLE_SELECT_MODE_MAP.VIN || !vin || vinParams.vin || vinParams.vin === null;

  return !!(styleParamsReady && vinParamsReady);
}

export function getVDPPriceData({ maxFairPrice, maxGoodPrice, maxGreatPrice, userDealerPrice }) {
  if (userDealerPrice > 0 && userDealerPrice <= maxGreatPrice) {
    return VDP_SUPPORTING_COPY[GREAT_DEAL];
  }
  if (userDealerPrice > 0 && userDealerPrice <= maxGoodPrice) {
    return VDP_SUPPORTING_COPY[GOOD_DEAL];
  }
  if (userDealerPrice > 0 && userDealerPrice <= maxFairPrice) {
    return VDP_SUPPORTING_COPY[FAIR_DEAL];
  }

  return VDP_SUPPORTING_COPY[ABOVE_MARKET](maxFairPrice);
}

export function getVDPPriceByDealType({ maxFairPrice, dealType }) {
  return dealType === ABOVE_MARKET ? VDP_SUPPORTING_COPY[ABOVE_MARKET](maxFairPrice) : VDP_SUPPORTING_COPY[dealType];
}

export function getTmvPrices(tmvData, isCore6830International) {
  return isCore6830International ? get(tmvData, 'nationalBasePrice') : get(tmvData, 'totalWithOptions');
}

export function getTmvChartData({
  tmvData,
  tmvAdjustment,
  userMsrp,
  userDealerPrice,
  bestDealPrice,
  isVDP,
  isCore6830International,
}) {
  const { maxFairPrice: maxFair, maxGoodPrice: maxGood, maxGreatPrice: maxGreat, dealType } = tmvData;
  let maxFairPrice;
  let maxGoodPrice;
  let maxGreatPrice;

  if (maxFair && maxGood && maxGreat) {
    maxFairPrice = isVDP ? roundToNearestTen(maxFair) : maxFair;
    maxGoodPrice = isVDP ? roundToNearestTen(maxGood) : maxGood;
    maxGreatPrice = isVDP ? roundToNearestTen(maxGreat) : maxGreat;
    const vdpDealerPrice =
      maxFair === maxGood
        ? getVDPPriceByDealType({ maxFairPrice, dealType })
        : getVDPPriceData({ maxFairPrice, maxGoodPrice, maxGreatPrice, userDealerPrice });

    return {
      maxFairPrice,
      maxGreatPrice,
      maxGoodPrice,
      dealerPrice: isVDP
        ? vdpDealerPrice
        : getPriceData({ maxFairPrice, maxGoodPrice, maxGreatPrice, bestDealPrice, userDealerPrice }),
    };
  }

  const tmvPrices = getTmvPrices(tmvData, isCore6830International);
  const msrp = get(tmvPrices, 'baseMSRP', 0);
  const tmv = get(tmvPrices, 'tmv', 0);
  const destinationFee = get(tmvData, 'destinationCharge', 0);
  const gasGuzzlerTax = get(tmvData, 'gasGuzzlerTax', 0);
  const primaryRebates = get(tmvData, 'incentivesAndRebates', 0);
  const currentMsrp = userMsrp || msrp;

  // Business logic below
  const maxGoodPriceMargin = get(tmvAdjustment, 'tmvAdjCorePercent') || parseFloat((tmv / msrp).toFixed(16)) || 0;
  const maxFairPriceMargin = maxGoodPriceMargin + 0.02;
  const maxGreatPriceMargin = maxGoodPriceMargin - 0.02;

  let basePrice = currentMsrp;
  let baseAdditions = 0;
  if (!isCore6830International) {
    basePrice = currentMsrp - (destinationFee + gasGuzzlerTax);
    baseAdditions = destinationFee + gasGuzzlerTax - get(tmvAdjustment, 'tmvAdjDealerCash', 0) - primaryRebates;
  }

  maxFairPrice = parseFloat((basePrice * maxFairPriceMargin + baseAdditions).toFixed(2));
  maxGoodPrice = parseFloat((basePrice * maxGoodPriceMargin + baseAdditions).toFixed(2));
  maxGreatPrice = parseFloat((basePrice * maxGreatPriceMargin + baseAdditions).toFixed(2));

  return {
    maxFairPrice,
    maxGreatPrice,
    maxGoodPrice,
    dealerPrice: getPriceData({
      maxFairPrice,
      maxGoodPrice,
      maxGreatPrice,
      userDealerPrice,
      isHighEsp: maxGoodPrice > currentMsrp,
    }),
  };
}

function getDescriptionPricePart({ minPrice, maxPrice, addSpace }) {
  if (!minPrice && !maxPrice) {
    return '';
  }

  if (minPrice) {
    if (maxPrice) {
      return `${addSpace ? ' ' : ''}between ${getPriceString(minPrice)} and ${getPriceString(maxPrice)}`;
    }
    return `${addSpace ? ' ' : ''}above ${getPriceString(minPrice)}`;
  }

  return `${addSpace ? ' ' : ''}below ${getPriceString(maxPrice)}`;
}

export function getPricingChartDescription(descriptionParams = []) {
  const descriptionParts = descriptionParams.map(tierParams =>
    tierParams.tierTransactionCount || tierParams.minPrice || tierParams.maxPrice
      ? `${tierParams.tierTransactionCount || ''} "${tierParams.tierLabel} Price" ${
          tierParams.tierTransactionCount ? 'sales' : ''
        }${getDescriptionPricePart({
          minPrice: tierParams.minPrice,
          maxPrice: tierParams.maxPrice,
          addSpace: !!tierParams.tierTransactionCount,
        })}`.trim()
      : ''
  );

  const description = compact(descriptionParts).join('; ').trim();

  return description ? `${description}.` : '';
}

export function getOutOfDoorPrice({
  tmvData,
  tmvAdjustment,
  userDealerPrice,
  userMsrp,
  bestDealPrice,
  totalTaxesFees,
  isCore6830International,
}) {
  if (!tmvData || !userDealerPrice || (!totalTaxesFees && !isCore6830International)) {
    return {};
  }

  const {
    dealerPrice: { label },
    maxGoodPrice,
  } = getTmvChartData({ tmvData, tmvAdjustment, userMsrp, userDealerPrice, bestDealPrice, isCore6830International });

  return label === DEAL_LABEL.GREAT || label === DEAL_LABEL.GOOD
    ? {
        outOfDoorPrice: userDealerPrice + totalTaxesFees,
        outOfDoorTooltipText:
          'This price is a sum of your Dealer price quote and estimated taxes and fees based on the ZIP code displayed.',
        descriptionText: 'If the dealer’s out-the-door price is higher, be sure to ask why.',
        isGoodPrice: true,
      }
    : {
        outOfDoorPrice: maxGoodPrice + totalTaxesFees,
        edmundsSuggestedPrice: maxGoodPrice,
        outOfDoorTooltipText:
          'This price is a sum of the Edmunds Suggested Price and estimated taxes and fees based on the ZIP code displayed.',
        descriptionText: 'Tell the dealer you want to pay this price, including taxes and fees.',
        isGoodPrice: false,
      };
}

export function getDealBarIndex({
  tmvData: { maxGreatPrice, maxGoodPrice, maxFairPrice },
  label,
  barsPerDeal,
  sliderValues,
  currentSliderValue,
}) {
  const greatPrice = Math.round(maxGreatPrice);
  const goodPrice = Math.round(maxGoodPrice);
  const fairPrice = Math.round(maxFairPrice);
  let leftValue;
  let rightValue;
  let startIndex;
  switch (label) {
    case GREAT_DEAL:
      leftValue = 0;
      rightValue = sliderValues.indexOf(greatPrice);
      startIndex = 0;
      break;
    case GOOD_DEAL:
      leftValue = sliderValues.indexOf(greatPrice) + 1;
      rightValue = sliderValues.indexOf(goodPrice);
      startIndex = barsPerDeal;
      break;
    case FAIR_DEAL:
      leftValue = sliderValues.indexOf(goodPrice) + 1;
      rightValue = sliderValues.indexOf(fairPrice);
      startIndex = barsPerDeal * 2;
      break;
    default:
      leftValue = sliderValues.indexOf(fairPrice) + 1;
      rightValue = sliderValues.length - 1;
      startIndex = barsPerDeal * 3;
  }

  const valuesPerBars = (rightValue - leftValue + 1) / barsPerDeal;
  return startIndex + Math.ceil((currentSliderValue - leftValue + 1) / valuesPerBars) - 1;
}

export function getDealIndex({
  tmvData: { maxGreatPrice, maxGoodPrice, maxFairPrice },
  userDealerPrice,
  lowerPayment,
  upperPayment,
  label,
  barsPerDeal,
}) {
  let leftValue;
  let rightValue;
  let startIndex;

  switch (label) {
    case GREAT_DEAL:
      leftValue = lowerPayment;
      rightValue = maxGreatPrice;
      startIndex = 0;
      break;
    case GOOD_DEAL:
      leftValue = maxGreatPrice;
      rightValue = maxGoodPrice;
      startIndex = barsPerDeal;
      break;
    case FAIR_DEAL:
      leftValue = maxGoodPrice;
      rightValue = maxFairPrice;
      startIndex = barsPerDeal * 2;
      break;
    default:
      leftValue = maxFairPrice;
      rightValue = upperPayment;
      startIndex = barsPerDeal * 3;
  }
  rightValue += 1; // to not get out of bounds

  const delta = ((rightValue - leftValue) / barsPerDeal).toFixed(2);
  const bar = Math.ceil((userDealerPrice - leftValue) / delta); // bar number inside deal type range (1-5)
  const index = startIndex + bar - 1; // -1 to get correct index on chart
  const maxColNum = CHART_PROPS_REDESIGN.DATA_POINTS.WITH_DATA.length - 1;

  if (index < 0) {
    return 0;
  } else if (index > maxColNum) {
    return maxColNum;
  }
  return index;
}

export function getSliderValues({ maxFairPrice, maxGoodPrice, maxGreatPrice }, dealerPrice, sectionLength) {
  const lowerPrice = maxGreatPrice - (maxGoodPrice - maxGreatPrice);
  const upperPrice = maxFairPrice + (maxFairPrice - maxGoodPrice);

  const sliderValues = [
    ...getPaymentsRange(lowerPrice, maxGreatPrice, sectionLength),
    ...getPaymentsRange(maxGreatPrice + 1, maxGoodPrice, sectionLength),
    ...getPaymentsRange(maxGoodPrice + 1, maxFairPrice, sectionLength),
    ...getPaymentsRange(maxFairPrice + 1, upperPrice, sectionLength),
  ];

  // make sure that user entered dealer price is added
  sliderValues.splice(getSliderClosestValue(sliderValues, dealerPrice), 1, dealerPrice);

  return sliderValues;
}

export const getXPosition = (value, sliderValuesLength) => (value / sliderValuesLength) * 100;

const PRICE_FIELDS = {
  great: 'maxGreatPrice',
  good: 'maxGoodPrice',
  fair: 'maxFairPrice',
};

export function getDealsIncludingIncentives({ dealItems = [], showLeasePayments }) {
  return dealItems.map(dealItem => {
    const dealOffer = get(dealItem, 'dealOffer');
    const incentives = get(dealOffer, 'incentives', []);

    const sellingPrice = dealOffer?.sellingPrice ?? 0;

    const totalRebates = getTotalRebates({ incentives, showLeasePayments });

    const purchasePriceAfterRebates = sellingPrice - totalRebates;

    return { ...dealItem, dealOffer: { ...dealItem.dealOffer, sellingPrice: purchasePriceAfterRebates } };
  });
}

export function getTmvData({ dealItems, isPurchase, showLeasePayments }) {
  const dealsIncludingIncentives = getDealsIncludingIncentives({
    dealItems: dealItems[isPurchase ? PURCHASE : LEASE],
    showLeasePayments: !isPurchase,
  });

  return DEALS.reduce((result, dealType) => {
    const dealItem = find(dealsIncludingIncentives, deal => get(deal, 'dealOffer.dealType') === dealType);
    const dealOffer = get(dealItem, 'dealOffer');
    const monthlyPayment = get(dealOffer, 'baseMonthlyPayment');

    const fieldName = PRICE_FIELDS[dealType];
    return {
      ...result,
      [fieldName]: showLeasePayments ? monthlyPayment : get(dealOffer, 'sellingPrice', 0),
    };
  }, {});
}

export function getTransactionCountCopyValues({
  transactionCount,
  styleParams,
  visitorLocation,
  isVdpPriceChecker,
  dealerState,
}) {
  const make = get(styleParams, 'makeName');
  const model = get(styleParams, 'modelName');
  const trim = get(styleParams, 'trim');
  const displayTrim = isVdpPriceChecker && !showTrimName(make, trim) ? '' : trim;
  let group = isVdpPriceChecker ? `${make} ${model} ${displayTrim}`.trim() : get(styleParams, 'styleName');
  switch (get(transactionCount, 'group')) {
    case 'make': {
      group = make;
      break;
    }
    case 'model': {
      group = isVdpPriceChecker ? `${make} ${model}` : model;
      break;
    }
    case 'style': {
      group = isVdpPriceChecker ? `${make} ${model} ${displayTrim}`.trim() : get(styleParams, 'styleName');
      break;
    }
    default: {
      break;
    }
  }

  let level;
  switch (get(transactionCount, 'level')) {
    case 'usa':
    case 'national': {
      level = 'the U.S.';
      break;
    }
    case 'state': {
      level = isVdpPriceChecker ? dealerState : get(visitorLocation, 'state');
      break;
    }
    case 'dma':
    default: {
      level = 'your area';
      break;
    }
  }

  return { group, level };
}

export function normalizeVin(value) {
  return typeof value === 'string' ? value.toUpperCase() : '';
}

export function transformVinDecoder({ styles, modelYear }, { vin } = {}) {
  return {
    vin,
    makeName: modelYear?.makeName,
    makeSlug: modelYear?.makeSlug,
    modelName: modelYear?.modelName,
    modelSlug: modelYear?.modelSlug,
    year: modelYear?.year,
    styleId: parseInt(styles?.[0]?.id, 10),
    styles: styles.map(({ id, name, trimName, slug }) => ({
      id: parseInt(id, 10),
      name,
      submodelName: modelYear?.modelName,
      trim: trimName,
      slug,
    })),
  };
}

export function getChartDescription({
  transactionsDetails: { hasTransactionCount, group, level } = {},
  hasTmvChartData,
  modelName,
  isCore6830International,
}) {
  if (isCore6830International) {
    return `${
      hasTmvChartData
        ? `This chart rates your price quote based on ${modelName} sales`
        : `This chart will rate your price quote based on sales`
    } up through ${getEspTrustDate()}, including destination fee. To check for local rebates, enter your`;
  }

  return `${
    hasTmvChartData && hasTransactionCount
      ? `This chart rates your price quote based on ${group} sales in ${level}`
      : `This chart will rate your price quote based on sales`
  } up through ${getEspTrustDate()}, including destination fee, and any rebates currently available near`;
}
